<template>
    <div v-if="active" class="card">        
        <a class="theme-primary help-icon icon text-right" href="login-help.html" target="_blank" alt="Login Help">
            <fa-icon :icon="['fas','question-circle']" /><span class="sr-only">login help</span>
        </a>
        <Error ref="error" />
        <div class="card-body">
            <PageHeader :title="title" variant="small" class="mb-4" />
            <p v-html="screenData.text.sentCode"></p>
            <label for="securityCode">{{screenData.text.securityCode}}</label>
            <input id="securityCode" ref="securityCode" type="text" v-bind:class="{'error': securityCodeError}" class="form-control" name="securityCode" v-model="tokenCode" @keyup.enter="verifyCode" @blur="onSecurityCodeBlur">
            <p v-if="securityCodeError" class="mt-2 errorMsg" role="alert" aria-live="assertive"><fa-icon :icon="['fas', 'exclamation-triangle']" class="mr-1" />{{screenData.text.enterOtp}}</p>
            <Timer ref="timer" :timer-active-text="screenData.text.timerActive" :resend-code-show="resendCodeShow" :resend-code-msg-text="screenData.text.resendCodeMsg"></Timer>
            <p v-if="hasResendAPI" class="row mx-0">{{screenData.text.noCode}}<a class="ml-1" href="" @click="resendCode">{{screenData.text.resendCode}}</a></p>
            <div class="custom-control custom-checkbox" v-if="!usesThreatMetricsOnly">
                <input type="checkbox" class="theme-primary custom-control-input" id="rememberDeviceCheck" @change="toggleRememberDevice">
                <label class="custom-control-label icon-label" for="rememberDeviceCheck">{{screenData.text.rememberDevice}}</label>&nbsp;
                <fa-icon :icon="['fas','info-circle']" tabindex="0" class="icon" aria-hidden="false" v-tooltip="screenData.text.rememberDeviceTooltip" />
                <p class="fine-print">{{screenData.text.doNotCheckRemember}}</p>
            </div>
            <ActionList :actions="screenData.actions" @verify-code="verifyCode" @select-new-method="selectNewMethod" @verify-code-cancel="cancel" />
        </div>
    </div>
</template>

<script>
    import { nextTick } from 'vue'
    import Error from '../entities/Error.vue';
    import Timer from '../entities/Timer.vue';
    import ActionList from '../entities/ActionList.vue';
    import PageHeader from '../entities/PageHeader.vue';
    import {PostAuthHandler} from '../mixins/PostAuthHandler.js';

    export default {
        name: "MultiFactorVerify",

        components: {
            Error,
            Timer,
            ActionList,
            PageHeader,
        },

        mixins: [
            PostAuthHandler
        ],

        props: {
            screenData: String,
            usesThreatMetricsOnly: Boolean
        },

        data: function() {
            return {
                active: false,
                flow: "",
                verifyTokenUrl: "",
                resendTokenUrl: "",
                cancelUrl: "",
                stateToken: "",
                tokenCode: "",
                rememberDevice: false,
                resendCodeShow: false,
                securityCodeError: null
            }
        },

        methods: {
            show: function(data) {
                this.flow = data.flow;
                this.verifyTokenUrl = data.verifyTokenUrl;
                this.resendTokenUrl = data.resendTokenUrl;
                this.cancelUrl = data.cancelUrl;
                this.stateToken = data.stateToken;
                this.timeRemaining = data.timeRemaining;
                this.active = true;
                nextTick(function() {
                    this.clear();
                    this.$refs["timer"].setTime(this.timeRemaining);
                    this.$refs["timer"].startTimer();
                    if (data.hideChangeDeliveryMethod || data.resendTokenUrl === "") {
                        this.$emitter.emit('hide','select-new-method');
                    }
                }.bind(this));
            },

            hide: function() {
                this.active = false;
            },

            clear: function() {
                this.tokenCode = "";
                this.securityCodeError = false;
            },

            onSecurityCodeBlur: function(){
                this.securityCodeError =  this.tokenCode === "";
                return this.securityCodeError;
            },

            resendCode: function(e) {
                e.preventDefault();
                this.$refs["error"].clear();
                var data = {
                    "stateToken": this.stateToken
                };

                this.$axios.post(this.resendTokenUrl, data).then(function(response) {
                    if (response.data.success) {
                        this.stateToken = response.data.data.stateToken;
                        if (response.data.data._links.resend !== undefined) {
                            this.resendTokenUrl = response.data.data._links.resend.href;
                        }
                        else {
                            this.resendTokenUrl = "";
                        }
                        this.verifyTokenUrl = response.data.data._links.next.href;
                        this.$refs["timer"].restartTimer(response.data.data._links.next.timeRemainInSeconds);
                        this.resendCodeShow = true;
                        this.$refs.securityCode.focus();
                    }
                }.bind(this));
            },

            verifyCode: function() {
                this.$refs["error"].clear();
                if (!this.$refs["timer"].isExpired()) {
                    this.$emitter.emit('start-loading','verify-code');
                    var data = {
                        "stateToken": this.stateToken,
                        "verifyToken": this.tokenCode
                    };

                    if (this.rememberDevice) {
                        data["rememberDevice"] = "Y";
                    }

                    this.$axios.post(this.verifyTokenUrl, data).then(function(response) {
                        if (response.data.success) {
                            this.$refs["timer"].stopTimer();
                            if (response.data.data.status === "SUCCESS") {
                                this.handleSuccessStatus(response.data.data._links);
                            }
                            else if (response.data.data.status === "MFA_ENROLL" && response.data.data.factorResult === "SUCCESS") {
                                this.handleSuccessStatus(response.data.data._links);
                            }
                            else if (response.data.data.status === "PASSWORD_RESET") {
                                var resetPasswordData = {
                                    stateToken: response.data.data.stateToken,
                                    api: response.data.data._links.next.next.href
                                };

                                this.$emitter.emit('navigate', { componentName: "resetPassword", routeData: resetPasswordData});
                                this.$emitter.emit('stop-loading','verify-code');
                            }
                            else if (response.data.data.status === "RETRIEVE_USER") {
                                var nextScreenData = {
                                    userId: response.data.data.userId,
                                    links: response.data.data._links
                                };

                                this.$emitter.emit('navigate', { componentName: "retrieveUser", routeData: nextScreenData});
                                this.$emitter.emit('stop-loading','verify-code');
                            }
                        }
                        else {
                            var err = response.data.messages[0];
                            if (err.errorCode === "1527") {
                                var data = {
                                    flow: this.flow,
                                    links: response.data.data._links,
                                    noBSL: true
                                };
                                this.$emitter.emit('stop-loading','verify-code');
                                this.$emitter.emit('navigate', { componentName: 'skipMfa', routeData: data});
                            }
                        }
                    }.bind(this)).catch(function(error) {
                        if (typeof error.response.data == 'string' || error.response.data.messages.length > 0) {
                        var err = typeof error.response.data == 'string' ? {
                                errorCode: error.response.status,
                                errorMessage: error.message
                            } : error.response.data.messages[0];
                            if (err.errorCode === "8004" || err.errorCode === "XA586231" || err.errorCode === "6231" || err.errorMessage === "Invalid Authentication") {
                                var data = {
                                    flow: this.flow,
                                    errorCode: err.errorCode,
                                    isSuspended: true
                                };
                                this.$emitter.emit('stop-loading','verify-code');
                                this.$emitter.emit('navigate', { componentName: 'accountHold', routeData: data});
                            }
                            else {
                                this.$refs["error"].display(err.errorCode, err.errorMessage, err.errorType);
                            }
                        }
                        this.$emitter.emit('stop-loading','verify-code');
                        this.tokenCode = "";
                        this.$refs.securityCode.focus();
                    }.bind(this));
                }
                this.resendCodeShow = false;
            },

            handleSuccessStatus: function(links) {                
                this.start(links);
            },

            selectNewMethod: function() {
                var data = {
                    flow: this.flow,
                    stateToken: this.stateToken,
                    cancelUrl: this.cancelUrl
                };

                this.$emitter.emit('navigate', { componentName: 'mfaSelect', routeData: data});
                this.resendCodeShow = false;
            },

            cancel: function() {
                this.$emitter.emit('start-loading','verify-code-cancel');
                window.location.href = window.location.href;
                this.resendCodeShow = false;
            },

            toggleRememberDevice: function(e) {
                this.rememberDevice = e.target.checked;
            }
        },

        computed: {
            hasResendAPI: function() {
                return this.resendTokenUrl !== "";
            },
            title: function() {
                return this.flow + ": " + this.screenData.text.cardTitle;
            }
        },

        watch: {
            hasResendAPI: function(val) {
                if (val) {
                    this.$emitter.emit('show','select-new-method');
                }
                else {
                    this.$emitter.emit('hide','select-new-method');
                }
            },

            tokenCode: function(val){
                this.securityCodeError = false;
                this.$emitter.emit(val ? 'enable' : 'disable', 'verify-code');
            }
        }
    }
</script>

<style scoped>

</style>